import React, { useMemo, useRef } from 'react';
import type { GanttData, Task, DependencyType } from '../types';
import { GanttRow } from './GanttRow';
import { DependencyLayer } from './DependencyLayer';
import { useDependencyDrag } from '../hooks/useDependencyDrag';

type Props = {
  data: GanttData;
  moveTaskDates: (taskId: string, startDate: string, endDate: string) => void;
  moveTaskToRow: (taskId: string, targetRowId: string) => void;
  createRow: () => { id: string };
  addDependency: (from: string, to: string, type: DependencyType) => void;
  onEditTask: (taskId: string) => void;
  hasUnresolvedDepsMap: Map<string, boolean>;
  // New Row Actions
  addRowAbove: (rowId: string) => void;
  addRowBelow: (rowId: string) => void;
  splitRow: (rowId: string) => void;
};

export const GanttGrid: React.FC<Props> = (props) => {
  const {
    data, moveTaskDates, moveTaskToRow, createRow, addDependency, onEditTask, hasUnresolvedDepsMap,
  } = props;
  const gridRef = useRef<HTMLDivElement>(null);
  const taskById = useMemo(() => new Map(data.tasks.map(t => [t.id, t])), [data.tasks]);
  const { activeDrag, handlePointClick } = useDependencyDrag({ addDependency, ganttRef: gridRef });

  return (
    <div ref={gridRef} data-gantt-grid="1" style={{ display: 'flex', flexDirection: 'column', position: 'relative', overscrollBehavior: "none" }}>
      <DependencyLayer data={data} activeDrag={activeDrag} />

      {data.rows.length === 0 ? (
        <div style={{ padding: '24px 16px', color: '#bbb', fontSize: 13, userSelect: 'none' }}>
          No tasks yet. Use the toolbar to add or import tasks.
        </div>
      ) : (
        data.rows.map(row => {
          const rowTasks = row.taskIds.map(id => taskById.get(id)).filter(Boolean) as Task[];
          return (
            <GanttRow
              key={row.id}
              row={row}
              tasks={rowTasks}
              moveTaskDates={moveTaskDates}
              moveTaskToRow={moveTaskToRow}
              createRow={createRow}
              onPointClick={handlePointClick}
              onEditTask={onEditTask}
              hasUnresolvedDepsMap={hasUnresolvedDepsMap}
              onAddRowAbove={props.addRowAbove}
              onAddRowBelow={props.addRowBelow}
              onSplitRow={props.splitRow}
            />
          );
        })
      )}
    </div>
  );
};
